import { splitKeys } from 'labels/utils/splitKeys'
import type { Labels, LabelsNamespaces } from 'types/labels'

interface FetchLabelsParams {
	country: string
	language: string
	service: string
	namespaces?: LabelsNamespaces[]
	keys?: string[]
}

const doFetch = (
	url: URL,
	locale: string
): Promise<{ translations: Labels }> => {
	return fetch(url.href, {
		next: { revalidate: 0 },
	})
		.then((res) => res.json())
		.catch(() => {
			throw new Error(
				`Error fetching labels on ${url.href} with locale ${locale}`
			)
		})
}

export const fetchLabels = async ({
	namespaces,
	keys,
	service,
	country,
	language,
}: FetchLabelsParams): Promise<Labels> => {
	if (!service) {
		throw new Error('service is not defined')
	}

	if (
		(!namespaces || namespaces.length === 0) &&
		(!keys || keys.length === 0)
	) {
		throw new Error('Either namespace or keys must be defined')
	}

	const locale = `${language}-${country.toUpperCase()}`
	const promises: Promise<Labels>[] = []
	if (keys) {
		const sortedKeys = [...new Set(keys)].sort((a: string, b: string) =>
			a.localeCompare(b)
		)

		promises.push(
			...splitKeys(sortedKeys).map(async (splitted) => {
				const url = new URL(`${service}/labels/v2/${splitted.join(',')}`)
				url.searchParams.append('languageIso', language)
				url.searchParams.append('countryIso', country.toUpperCase())
				const response = await doFetch(url, locale)
				return response.translations
			})
		)
	}
	if (namespaces) {
		const sortedNamespaces = [...new Set(namespaces)].sort(
			(a: LabelsNamespaces, b: LabelsNamespaces) => a.localeCompare(b)
		)
		promises.push(
			...sortedNamespaces.map(async (namespace) => {
				const url = new URL(`${service}/labels/v2/namespace/${namespace}`)
				url.searchParams.append('languageIso', language)
				url.searchParams.append('countryIso', country.toUpperCase())
				const response = await doFetch(url, locale)
				return response.translations
			})
		)
	}
	const responses = await Promise.all(promises)
	return responses.reduce((acc, curr) => ({ ...acc, ...curr }), {})
}
